import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Check, Edit } from 'lucide-react'
import { Link } from 'react-router-dom'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import capitalize from 'lodash/capitalize'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterRole } from 'app/frontend/types/custom'
import { Checkbox } from '@/components/ui/checkbox'
import { useTeam } from 'app/frontend/base/config/context/team'
import type { Specialization } from 'app/frontend/types/character_values'

type RankMenuItemsProps = {
  member: Partial<wowaudit.TeamMembership>
  onRankChange: (rankId: number) => void
}

export const RankMenuItems = ({ member, onRankChange }: RankMenuItemsProps) => {
  const { team } = useTeam()

  return (
    <>
      {team.teamRanks?.map((rank) => (
        <DropdownMenuItem key={rank.id} onClick={() => onRankChange(rank.id)}>
          <div
            className={`flex items-center gap-2 justify-between w-full ${
              member.teamRankId === rank.id ? 'text-primary' : ''
            }`}
          >
            <span>{rank.name}</span>
            {member.teamRankId === rank.id && <Check className='w-4 h-4' />}
          </div>
        </DropdownMenuItem>
      ))}

      <DropdownMenuSeparator />

      <Link to={`${team.path}/settings#roster`}>
        <DropdownMenuItem>
          <span className='flex items-center gap-2 opacity-70'>
            <Edit className='w-4 h-4' /> Manage ranks
          </span>
        </DropdownMenuItem>
      </Link>
    </>
  )
}

type MainSpecMenuItemsProps = {
  member: Partial<wowaudit.TeamMembership>
  classSpecializations: Record<string, Specialization>
  rolesWithMultipleSpecs: string[]
  onRoleChange: (role: CharacterRole) => void
  onSpecChange: (specId: number) => void
}

export const MainSpecMenuItems = ({
  member,
  classSpecializations,
  rolesWithMultipleSpecs,
  onRoleChange,
  onSpecChange,
}: MainSpecMenuItemsProps) => {
  return (
    <>
      {rolesWithMultipleSpecs?.map((role) => (
        <DropdownMenuItem
          key={role}
          onClick={() => onRoleChange(role as CharacterRole)}
        >
          <div
            className={`flex items-center gap-2 w-full ${
              member.role === role && member.specId === null
                ? 'text-primary'
                : ''
            }`}
          >
            <CharacterRoleIcon role={role as CharacterRole} className='mr-8' />
            <span>{capitalize(role)}</span>
          </div>
        </DropdownMenuItem>
      ))}

      {Object.entries(classSpecializations).map(([specId, spec]) => (
        <DropdownMenuItem
          key={specId}
          onClick={() => onSpecChange(Number(specId))}
        >
          <div
            className={`flex items-center gap-2 w-full ${
              member.specId === Number(specId) ? 'text-primary' : ''
            }`}
          >
            <CharacterRoleIcon role={spec.default_role} />
            <BlizzardIcon iconName={spec.icon} />
            <span>{spec.name}</span>
          </div>
        </DropdownMenuItem>
      ))}
    </>
  )
}

type OffSpecMenuItemsProps = {
  member: Partial<wowaudit.TeamMembership>
  classSpecializations: Record<string, Specialization>
  onOffSpecChange: (specId: number) => void
}

export const OffSpecMenuItems = ({
  member,
  classSpecializations,
  onOffSpecChange,
}: OffSpecMenuItemsProps) => {
  return (
    <>
      {Object.entries(classSpecializations).map(([specId, spec]) => {
        if (
          member.specId === Number(specId) ||
          (member.role === spec.default_role && member.specId === null)
        )
          return null

        return (
          <DropdownMenuItem
            key={specId}
            onClick={() => onOffSpecChange(Number(specId))}
          >
            <div
              className={`flex items-center gap-2 w-full ${
                member.offspecIds?.includes(Number(specId))
                  ? 'text-primary'
                  : 'text-muted-foreground'
              }`}
            >
              <CharacterRoleIcon role={spec.default_role} />
              <BlizzardIcon iconName={spec.icon} />
              <div className='grow'>{spec.name}</div>
              <Checkbox
                className='ml-2 border-accent bg-card-nested data-[state=checked]:border-primary'
                checked={member.offspecIds?.includes(Number(specId))}
              />
            </div>
          </DropdownMenuItem>
        )
      })}
    </>
  )
}
